<template>
	<div>
		<app-app-header v-model="indexStore.isHeaderVisible"
						:title="clubStore.club.display_name"
						:breadcrumbs="[{text: clubStore.club.parentClub ? $t('label.department') : clubStore.club.presentation_type_name, to: $web('clubs.index')}]"
						:image="{path: clubStore.club.avatar_thumbnail}"
						:color="clubStore.club.color ?? $config.public.colors.secondary"
						:to="$web('clubs.show', [clubStore.club._key])">

			<div class="flex items-center justify-end">
				<div class="w-88">
					<client-only>
						<template v-if="clubStore.authPermissions.is_initialized">
							<btn v-if="clubStore.authPermissions.is_member" :to="$web('clubs.show.membership', clubStore.club._key)"
								 :value="true"
								 class="w-full" small
								 :color="clubStore.club.color ? clubStore.club.color : 'success'">
								<span>{{ $t('label.member_page') }}</span>
							</btn>

							<follow-text-button v-else :model="clubStore.club" :color="clubStore.club.color" small class="w-full" @follow="() => updateFollowingState(true)"
												@unfollow="() => updateFollowingState(false)"/>
						</template>
					</client-only>
				</div>

				<lazy-dropdown bottom nudge-right :menu-title="clubStore.club.display_name" :actions="actions">
					<btn fab :aria-label="$t('label.actions')" class="ml-12 sm:ml-16">
						<icon :icon="icons.options"/>
					</btn>
				</lazy-dropdown>
			</div>
		</app-app-header>

		<app-content>
			<page-content xs-full sm-full :has-plain-footer="!$device.isNative" :background="clubStore.club.background_color ?? '#FBFBFB'">
				<lazy-cdn-background-image v-if="clubStore.club.background_image && $device.isDesktop" class="fixed w-screen h-screen top-0 left-0 right-0 bottom-0"
										   :src="clubStore.club.background_image"
										   :ratio="clubStore.club.background_image.ratio" :width="1920" cover immediate/>

				<container xs-full sm-full class="stretch-layout">
					<card :style="`border-top: 2px solid ${clubStore.club.color ?? $config.public.colors.secondary}`" xs-tile sm-tile>
						<card-image v-if="clubStore.club.cover_image" :size="{ratio: clubStore.club.cover_image.ratio, width: 400, sm: {width: 868}, lg: {width: 1168}}"
									:src="clubStore.club.cover_image"
									:style="{backgroundColor: clubStore.club.color ?? '#bebebe', borderBottom: `1px solid ${clubStore.club.color ?? $config.public.colors.secondary}`}"
									class="w-full absolute md:relative top-0"/>

						<card-text :class="['pb-16 md:pb-24', clubStore.club.cover_image ? 'pt-48 md:pt-32' : 'pt-20 md:pt-32']">
							<div class="flex flex-column items-center md:flex-row justify-between">
								<div class="flex items-center flex-column md:flex-row">
									<profile-avatar v-model="clubStore.avatarModal"
													:src="clubStore.club.avatar"
													:thumbnail-src="clubStore.club.avatar_thumbnail"
													:editable="clubStore.authPermissions.can_edit" @updated="avatarUpdated"
													:action="`/v1/clubs/${clubStore.club.id}/design`"
													class="md:mr-32 mb-16 md:mb-0"/>
									<div class="mb-16 md:mb-0">
										<p class="text-subtext-color text-center md:text-left mb-4 leading-normal">
											<span v-if="clubStore.club.city">{{ $t('label.type_from_city', {type: clubStore.club.presentation_type_name, city: clubStore.club.city}) }}</span>
											<span v-else>{{ clubStore.club.presentation_type_name }}</span>
										</p>

										<div class="flex font-heading text-heading-color font-bold leading-normal text-2xl md:text-3xl mb-4 items-center justify-center md:justify-start text-center md:text-left">
											<span>{{ clubStore.club.display_name }}</span>
											<tooltip v-if="clubStore.club.verified" :text="$t('label.verified')" with-touch>
												<icon :icon="icons.verified" color="success" class="ml-8 mb-2" small/>
											</tooltip>
										</div>

										<sport-type-text :sport-types="clubStore.club.sportTypes" :truncate="2"
														 class="text-subtext-color text-center md:text-left justify-center md:justify-start leading-wide"/>

									</div>
								</div>

								<div class="w-full md:w-unset flex flex-column md:flex-row justify-center md:justify-end items-center">
									<nuxt-link class="mx-12 flex flex-column items-center justify-center" :to="$web('clubs.followers.index', clubStore.club._key)">
										<p class="font-bold mb-4 text-text-color text-xl leading-none font-heading">{{ clubStore.club.followers_count }}</p>
										<span class="text-subtext-color text-2xs leading-none">
											{{ $t('label.fans') }}
										</span>
									</nuxt-link>

									<div v-if="$device.isDesktop" class="flex mt-16 md:mt-0 md:ml-20 h-40">
										<div class="w-280">
											<client-only>
												<template v-if="clubStore.authPermissions.is_initialized">
													<btn v-if="clubStore.authPermissions.is_member" :to="$web('clubs.show.membership', clubStore.club._key)" class="w-full"
														 :value="true"
														 :color="clubStore.club.color ? clubStore.club.color : 'success'">
														<span>{{ $t('label.my_member_page') }}</span>
													</btn>
													<btn v-else-if="clubStore.authPermissions.has_pending_invitation" :to="$web('clubs.members.join', clubStore.club._key)"
														 class="w-full" color="success">
														<icon :icon="icons.member"/>
														<span>{{ $t('action.respond_invitations') }}</span>
													</btn>
													<btn-group v-else class="w-full">
														<follow-text-button :model="clubStore.club" :color="clubStore.club.color"
																			class="flex-grow-x-1"
																			@follow="() => updateFollowingState(true)" @unfollow="() => updateFollowingState(false)"/>
														<btn v-if="clubStore.authPermissions.has_pending_request" :to="$web('clubs.members.join', clubStore.club._key)"
															 :color="clubStore.club.color ?? $config.public.colors.secondary" outlined>
															<span>{{ $t('action.manage_requests') }}</span>
														</btn>
														<btn v-else-if="clubStore.club.is_managed && clubStore.club.defaultMemberJob" @click="openApplyModal"
															 :color="clubStore.club.color ?? $config.public.colors.secondary" outlined>
															<span>{{ $t('action.become_a_member') }}</span>
														</btn>
													</btn-group>
												</template>
											</client-only>
										</div>

										<dropdown bottom nudge-right min-width="200" class="ml-8" :actions="actions">
											<btn outlined :color="clubStore.club.color ?? $config.public.colors.secondary" :aria-label="$t('label.actions')">
												<icon :icon="icons.options"/>
											</btn>
										</dropdown>
									</div>

								</div>
							</div>
						</card-text>

						<header-toolbar id="tabs" sticky-mobile>
							<tabs class="h-full" grow>
								<tab v-for="(tabChildRoute, tabKey) in tabRoutes" replace :color="clubStore.club.color ?? $config.public.colors.secondary"
									 :exact="tabChildRoute.exact"
									 :key="tabKey" :to="tabChildRoute.to" :text="tabChildRoute.title"/>
							</tabs>
						</header-toolbar>
					</card>

					<!-- To allow the devices to scroll to the tabs even though a page has no content, we need to set the min height for our mobile profile page tab focus scrolling logic. -->
					<!-- 56px = header; 42px = tabs bar; 48px = mobile bottom bar; 8px = spacing top -->
					<div id="content" class="stretch-layout" :style="{minHeight: $device.isDesktop ? null : 'calc(100vh - 56px - 42px - 48px - 8px)'}">
						<NuxtPage keep-alive/>
					</div>

				</container>
			</page-content>
		</app-content>

		<template v-if="!clubStore.club.is_managed">
			<template v-if="$device.isMobile">
				<qmn-ad :config="QmnAds.MobilePageTracker" class="none"/>
			</template>
			<template v-else>
				<qmn-ad :config="QmnAds.DesktopSitebarAd"/>
				<qmn-ad :config="QmnAds.DesktopPageTracker" class="none"/>
			</template>
		</template>

		<lazy-job-application-form-modal v-if="showApplyModal && clubStore.club.defaultMemberJob" v-model="showApplyModal" :group="clubStore.club" :job="clubStore.club.defaultMemberJob"
										 :title="$t('action.request_membership')" @success="handleRequestedMembership"/>
	</div>
</template>

<script setup lang="ts">
import {mdiAccountGroup, mdiCheck, mdiClose, mdiShieldCheck} from '@mdi/js';
import {useIndexStore} from "../../store";
import {useClubStore} from "../../store/club";
import {optionsIcon} from "@spoferan/nuxt-spoferan/icons";
import {useGoogleSchema} from "../../composables/useGoogleSchema";
import {QmnAds} from "../../config/qmn";

const config = useRuntimeConfig();
const route = useRoute();
const {$api, $image, $date, $auth, $device, $web, $analytics} = useNuxtApp();
const indexStore = useIndexStore();
const clubStore = useClubStore();
const {t} = useI18n();
const {getClubOrganizationSchema, getClubSeoDescription} = useGoogleSchema();

const {value: cacheBusterValue} = useCacheBuster('club_ts');

// Refresh permissions on user change as required when user gets authenticated on clients side after SSR hydration
watch(() => $auth.token, async () => {
	await clubStore.refreshAuthPermissions();

	// Load the account if necessary
	if ((clubStore.authPermissions.is_member || ($auth.user && $auth.user.is_admin))) {
		const {data: account} = await useApiFetch($api('clubs.account.show', route.params.club))
		clubStore.account = account;
	} else {
		clubStore.account = null;
	}
}, {
	// Run immediately when token is already set (CSR) or the token will not be set as the user is not logged in
	immediate: !!$auth.token || !$auth.loggedIn
});

const {data: club} = await useApiFetch(`/v1/views/clubs/${route.params.club}`, {
	guest: true,
	params: {
		ts: cacheBusterValue,
	}
});

// If a hashed '#' char specified as '%23' is given in the URL, we do not want it for comparison as this would cause infinite redirects
const plainClubParam = route.params.club.split('#')[0];
if (club && club._key !== plainClubParam) {
	await navigateTo({params: {club: club._key}}, {
		redirectCode: 308
	});
}

clubStore.club = club;

const icons = {
	accept: mdiCheck,
	decline: mdiClose,
	options: optionsIcon,
	member: mdiAccountGroup,
	verified: mdiShieldCheck
};

const showApplyModal = ref(false);

const tabRoutes = computed(() => {
	const tabs = [
		{to: $web('clubs.show.index', clubStore.club._key), title: t('label.info'), exact: true},
		{to: $web('clubs.show.feed', clubStore.club._key), title: 'Feed'},
	];

	if (clubStore.club.news_cache_buster) {
		tabs.push({to: $web('clubs.show.news', clubStore.club._key), title: t('label.news')});
	}
	if (clubStore.club.blog_cache_buster) {
		tabs.push({to: $web('clubs.show.blog', clubStore.club._key), title: 'Blog'});
	}
	if (clubStore.club.events_count) {
		tabs.push({to: $web('clubs.events.index', clubStore.club._key), title: t('label.events')});
	}

	if (clubStore.club.accepted_members_count) {
		tabs.push({to: `/clubs/${clubStore.club._key}/results`, title: t('label.results')});
		tabs.push({to: `/clubs/${clubStore.club._key}/competition-planner`, title: t('label.competition_planner')});

		if (clubStore.club.has_public_members) {
			tabs.push({to: $web('clubs.members.index', clubStore.club._key), title: t('label.members'), exact: true});
		}
	}

	if (clubStore.club.is_managed) {
		tabs.push({to: $web('clubs.show.contact', clubStore.club._key), title: t('label.contact')});
	}

	return tabs;
});

const authQueryParamSuffix = computed(() => {
	// We pass the auth token for links to our external services to keep the user authenticated
	if ($device.isNative && $auth.token) {
		return `?auth=${$auth.token}`
	}

	return '';
});

const actions = computed(() => {
	return [
		clubStore.club.is_managed && !clubStore.authPermissions.is_member && clubStore.club.defaultMemberJob ? {
			name: t('action.become_a_member'),
			callback: openApplyModal
		} : undefined,
		clubStore.club.is_managed && $auth.loggedIn && (clubStore.authPermissions.has_pending_invitation || clubStore.authPermissions.has_pending_request) ? {
			name: clubStore.authPermissions.has_pending_invitation ? t('action.respond_invitations') : t('action.manage_requests'),
			to: $web('clubs.members.join', clubStore.club._key)
		} : undefined,
		clubStore.authPermissions.is_member || clubStore.authPermissions.can_edit ? {
			name: t('action.manage'),
			to: config.public.teamsUrl.replace('{team}', clubStore.club._key) + authQueryParamSuffix.value
		} : undefined,
		clubStore.authPermissions.is_member ? {
			name: t('label.my_member_page'),
			to: $web('clubs.show.membership', clubStore.club._key)
		} : undefined,
		{
			name: t('action.share'),
			callback: () => {
				indexStore.share({
					id: `club-${clubStore.club._key}`
				})
			}
		}
	].filter(Boolean);
});

function openApplyModal() {
	showApplyModal.value = true;
}

function avatarUpdated(clubWithNewAvatar) {
	clubStore.$patch((state) => {
		state.club.avatar_thumbnail = clubWithNewAvatar.avatar_thumbnail;
			state.club.avatar = clubWithNewAvatar.avatar;
			state.club.color = clubWithNewAvatar.color;
	})
}

function updateFollowingState(isFollowing) {
	clubStore.club.followers_count = isFollowing ? (clubStore.club.followers_count + 1) : (clubStore.club.followers_count - 1);
}

function handleRequestedMembership() {
	clubStore.authPermissions.has_pending_request = true;

	$analytics.trackEvent('join_group', {
		group_id: `${clubStore.club.model_name}-${clubStore.club.id}`,
		group_name: clubStore.club.display_name
	});
}

onMounted(() => {
	if (!clubStore.club.is_managed) {
		$analytics.trackQmnPageImpression();
	}
});

useMeta({
	title: clubStore.buildPageTitle(clubStore.club.display_name),
	description: getClubSeoDescription(clubStore.club),
	type: 'profile',
	meta: [{
		'property': 'og:profile:username',
		'content': clubStore.club.display_name
	}],
	image: clubStore.club.avatar_thumbnail ? $image.format(clubStore.club.avatar_thumbnail, {width: 1200, height: 630, fit: 'contain'}) : null,
	noIndex: !clubStore.club.is_public // Hide page for search engines, if not visible for public
});

useSchemaOrg({
	...getClubOrganizationSchema(clubStore.club.parentClub ? clubStore.club.parentClub : clubStore.club),
	department: clubStore.club.parentClub ? getClubOrganizationSchema(clubStore.club) : undefined
})
</script>

